import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { initFigmaOAuthSaga, clearFigmaTokenSaga, initSlackOAuthSaga, disconnectSlackSaga } from '@/store/workspace/workspaceSlice'
import { selectFigmaToken, selectSlackConnection } from '@/store/workspace/workspaceSelectors'
import { selectIsLoading } from '@/store/ui/uiSelectors'
import { selectMembers } from '@/store/workspace/workspaceSelectors'
import WorkspaceSettingsTitle from '../WorkspaceSettingsTitle/WorkspaceSettingsTitle'
import FormDivider from '@/components/ui/Form/components/FormDivider/FormDivider'
import FormColumn from '@/components/ui/Form/components/FormColumn/FormColumn'
import Button from '@/components/ui/Button/Button'
import { TextNorm, TextNormBold, TextSmall } from '@/components/ui/Text'
import FigmaIcon from '@/assets/icons/icon-figma.svg?react'
import ClaudeIcon from '@/assets/icons/icon-claude.svg?react'
import SlackIcon from '@/assets/icons/icon-slack.svg?react'
import OpenAIIcon from '@/assets/icons/icon-openai.svg?react'
import css from './WorkspaceSettingsModalIntegrations.module.css'

const MCP_SERVER_URL = `${window.location.origin}/mcp`
const CHATGPT_APP_URL = 'https://chatgpt.com/apps/plate/asdk_app_6a25adb4ed6881919eb1f9f08ce9e53f'

// Small "Documentation" link shown on each integration card (SCD-754).
const DocsLink = ({ href, label = 'Documentation' }: { href: string; label?: string }) => (
    <a className={css.docs_link} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
)

const WorkspaceSettingsModalIntegrations = () => {
    const dispatch = useAppDispatch()
    const figmaToken = useAppSelector(selectFigmaToken)
    const isConnecting = useAppSelector(selectIsLoading('figma-oauth-init'))
    const slack = useAppSelector(selectSlackConnection)
    const isSlackConnecting = useAppSelector(selectIsLoading('slack-oauth-init'))
    const isSlackDisconnecting = useAppSelector(selectIsLoading('slack-disconnect'))
    const members = useAppSelector(selectMembers)
    const currentUserId = useAppSelector(state => state.auth?.id)
    const currentMember = members.find((m: { userId: string; role: string }) => m.userId === currentUserId)
    const isOwner = currentMember?.role === 'owner'

    const handleCopyMcpUrl = () => {
        navigator.clipboard.writeText(MCP_SERVER_URL)
    }

    return (
        <>
            <WorkspaceSettingsTitle>Integrations</WorkspaceSettingsTitle>
            <FormDivider />
            <FormColumn>
                <div className={css.card}>
                    <div className={css.card_header}>
                        <div className={css.card_icon}>
                            <FigmaIcon />
                        </div>
                        <div className={css.card_info}>
                            <TextNormBold>Figma</TextNormBold>
                            {figmaToken && <span className={css.badge}>Connected</span>}
                        </div>
                    </div>
                    <TextNorm className={css.description}>
                        Paste a Figma frame URL in any description or comment to display an artboard preview.
                    </TextNorm>

                    {!isOwner ? (
                        <TextSmall className={css.owner_note}>
                            Only workspace owners can configure integrations.
                        </TextSmall>
                    ) : figmaToken ? (
                        <Button
                            title="Disconnect"
                            type="white"
                            size="medium"
                            onClick={() => dispatch(clearFigmaTokenSaga())}
                        />
                    ) : (
                        <Button
                            title="Connect with Figma"
                            type="black"
                            size="medium"
                            loading={isConnecting}
                            onClick={() => dispatch(initFigmaOAuthSaga())}
                        />
                    )}
                    <DocsLink href="/docs/apps/figma" />
                </div>

                <div className={css.card}>
                    <div className={css.card_header}>
                        <div className={css.card_icon}>
                            <SlackIcon />
                        </div>
                        <div className={css.card_info}>
                            <TextNormBold>Slack</TextNormBold>
                            {slack.connected && (
                                <span className={css.badge}>
                                    {slack.teamName ? `Connected · ${slack.teamName}` : 'Connected'}
                                </span>
                            )}
                        </div>
                    </div>
                    <TextNorm className={css.description}>
                        Get task notifications in your channels, create tasks from messages, and ask the @plate agent — without leaving Slack.
                    </TextNorm>

                    {!isOwner ? (
                        <TextSmall className={css.owner_note}>
                            Only workspace owners can configure integrations.
                        </TextSmall>
                    ) : slack.connected ? (
                        <Button
                            title="Disconnect"
                            type="white"
                            size="medium"
                            loading={isSlackDisconnecting}
                            onClick={() => dispatch(disconnectSlackSaga())}
                        />
                    ) : (
                        <Button
                            title="Add to Slack"
                            type="black"
                            size="medium"
                            loading={isSlackConnecting}
                            onClick={() => dispatch(initSlackOAuthSaga())}
                        />
                    )}

                    {isOwner && slack.connected && slack.notifyChannelName && (
                        <TextSmall className={css.owner_note}>
                            Task updates post to #{slack.notifyChannelName}. To change it, disconnect and reconnect Slack.
                        </TextSmall>
                    )}
                    <DocsLink href="/docs/apps/slack" />
                </div>

                <div className={css.card}>
                    <div className={css.card_header}>
                        <div className={css.card_icon}>
                            <ClaudeIcon />
                        </div>
                        <div className={css.card_info}>
                            <TextNormBold>Claude / Cursor / Windsurf</TextNormBold>
                        </div>
                    </div>
                    <TextNorm className={css.description}>
                        Manage Plate tasks using AI. Add the MCP server URL to Claude Desktop, Cursor, or any MCP-compatible client.
                    </TextNorm>
                    <div className={css.mcp_url_row}>
                        <TextSmall className={css.mcp_url}>{MCP_SERVER_URL}</TextSmall>
                        <Button
                            title="Copy URL"
                            type="white"
                            size="medium"
                            onClick={handleCopyMcpUrl}
                        />
                    </div>
                    <DocsLink href="/docs/mcp" />
                </div>

                <div className={css.card}>
                    <div className={css.card_header}>
                        <div className={css.card_icon}>
                            <OpenAIIcon />
                        </div>
                        <div className={css.card_info}>
                            <TextNormBold>ChatGPT</TextNormBold>
                        </div>
                    </div>
                    <TextNorm className={css.description}>
                        Plate is an official ChatGPT app. Manage your projects and tasks right inside ChatGPT — just by chatting.
                    </TextNorm>
                    <Button
                        title="Connect"
                        type="black"
                        size="medium"
                        onClick={() => window.open(CHATGPT_APP_URL, '_blank', 'noopener,noreferrer')}
                    />
                    <DocsLink href="/docs/apps/chatgpt" />
                </div>
            </FormColumn>
        </>
    )
}

export default WorkspaceSettingsModalIntegrations
